import "ag-grid-community/styles/ag-grid.css"; // Mandatory CSS required by the grid
import "ag-grid-community/styles/ag-theme-balham.css"; // Optional Theme applied to the grid
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";

import { globalAGGridTableHeight } from "@/components/widgets/tradeActionSummary/helper/actionSummaryHelper";

import { formatNumberWithCommas } from "@/lib/utilities";
import { IBaseAnalyticsCombined } from "@/store/analyticsDataStore";
import { useTheme } from "next-themes";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { cubeCustomColors } from "../../../../../tailwind.config";
import { defaultColDefAnalytics } from "../TableGenerics";
import CounterpartyBarChart from "./CounterpartyBarChart";
import {
  GetDetailRowDataParams,
  GetRowIdParams,
  ICellRendererParams,
  IDetailCellRendererParams,
} from "ag-grid-community";

function GenerateCounterpartyTables(theme: string | undefined) {
  if (theme == "light") {
    return {
      "--ag-background-color": cubeCustomColors.cubeTableRowColour[750],
      "--ag-odd-row-background-color": cubeCustomColors.cubeTableRowColour[750],
      "--ag-row-hover-color": cubeCustomColors.cubeTableRowColour[600], // Hover color
    };
  } else {
    return {
      "--ag-background-color": "transparent",
      "--ag-odd-row-background-color": "transparent",
      "--ag-row-hover-color": cubeCustomColors.cubeTableRowColour[800], // Hover color
    };
  }
}

function buildColDefs() {
  const colDefState = [
    {
      // checkboxSelection: true,
      enableCellChangeFlash: false,
      maxWidth: 30,
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
      // lockPosition: "left", // This ensures the column stays in the first position
      suppressColumnsToolPanel: true,
      cellRenderer: "agGroupCellRenderer",
      // headerCheckboxSelectionFilteredOnly: true,
      // headerCheckboxSelection: true,
      // mainMenuItems: ["resetColumns"],
    },
    { field: "counterparty", colId: "Counterparty", maxWidth: 120 },
    { field: "trades", colId: "#Trades", maxWidth: 110 },
    { field: "tradesWon", colId: "TradesWon", maxWidth: 110 },
    {
      field: "winRate",
      colId: "WinRate",
      maxWidth: 110,
      valueGetter: (params: any) => {
        const value = params.data.tradesWon / params.data.trades;
        const rate = value * 100;
        return rate.toFixed();
      },
      cellRenderer: (params: any) => `${params.value} %`,
    },
    {
      field: "DV01Seen",
      headerName: "DV01 Shown",
      colId: "DV01Seen",
      maxWidth: 130,
      cellRenderer: (params: any) => {
        if (!params.value) {
          return;
        }
        return `$ ${formatNumberWithCommas(params.value)}`;
      },
    },
    {
      field: "DV01Won",
      headerName: "DV01 Executed",
      colId: "DV01Won",
      maxWidth: 130,
      cellRenderer: (params: any) => {
        if (!params.value) {
          return;
        }
        return `$ ${formatNumberWithCommas(params.value)}`;
      },
    },
    {
      field: "DV01Rate",
      headerName: "DV01 Executed %",
      colId: "DV01Rate",
      maxWidth: 130,
      valueGetter: (params: any) => {
        const won = params.data.DV01Won;
        const seen = params.data.DV01Seen;
        const ratio = won / seen;
        const percentage = (ratio * 100).toFixed(2);

        return percentage;
      },
      cellRenderer: (params: any) => {
        if (!params.value) {
          return "";
        }
        return `${params.value} %`;
      },
    },
    {
      field: "spreadTotal",
      colId: "AverageSpread(bps)",
      headerName: "Average Spread (bps)",
      maxWidth: 140,
      valueGetter: (params: any) => {
        const value = params.data.spreadTotal / params.data.blocksWonTally;
        const rate = value;
        if (!rate) {
          return;
        }
        return Math.round(rate);
      },
      cellRenderer: (params: any) => {
        if (!params.value) {
          return "";
        }
        return `${params.value} bps`;
      },
    },
  ];
  return colDefState;
}

const CounterpartyMainContent = ({
  filteredData,
}: {
  filteredData: IBaseAnalyticsCombined[];
}) => {
  const { theme } = useTheme();
  const gridRef = useRef<AgGridReact>(null);

  const [colDefs] = useState(buildColDefs());

  const style = useMemo(
    () => GenerateCounterpartyTables(theme) as React.CSSProperties,
    [theme],
  );

  const getRowId = useCallback(
    (params: GetRowIdParams) => {
      if (params.data.counterparty != undefined) {
        return params.data.counterparty;
      }
    },
    [filteredData],
  );

  const detailCellRendererParams = useMemo(() => {
    return (_params: ICellRendererParams) => {
      const res = {} as IDetailCellRendererParams | any;
      res.getDetailRowData = (params: GetDetailRowDataParams<any>) => {
        params.successCallback(params.data.DV01Buckets);
      };
      res.detailGridOptions = {
        getRowId: (nestedParams: GetRowIdParams<any>) => {
          return nestedParams.data.index;
        },
        detailRowAutoHeight: true,
        columnDefs: [
          {
            field: "index",
            // checkboxSelection: true,
            enableCellChangeFlash: false,
            maxWidth: 30,
            // sortable: false,
            sort: "asc",
            filter: false,
            resizable: false,
            suppressMovable: true,
            cellRenderer: () => {
              return;
            },
            // lockPosition: "left", // This ensures the column stays in the first position
            // suppressColumnsToolPanel: true,
            // cellRenderer: "agGroupCellRenderer",
            // headerCheckboxSelectionFilteredOnly: true,
            // headerCheckboxSelection: true,
            // mainMenuItems: ["resetColumns"],
          },
          { field: "bucket", colId: "Counterparty", maxWidth: 120 },
          { field: "trades", colId: "#Trades", maxWidth: 110 },
          { field: "tradesWon", colId: "TradesWon", maxWidth: 110 },
          {
            field: "winRate",
            colId: "WinRate",
            maxWidth: 110,
            valueGetter: (params: any) => {
              const value = params.data.tradesWon / params.data.trades;
              const rate = value * 100;
              return rate.toFixed();
            },
            cellRenderer: (params: any) => `${params.value} %`,
          },
          {
            field: "DV01Seen",
            headerName: "DV01 Shown",
            colId: "DV01Seen",
            maxWidth: 130,
            cellRenderer: (params: any) => {
              if (!params.value) {
                return;
              }
              return `$ ${formatNumberWithCommas(params.value)}`;
            },
          },
          {
            field: "DV01Won",
            headerName: "DV01 Executed",
            colId: "DV01Won",
            maxWidth: 130,
            cellRenderer: (params: any) => {
              if (!params.value) {
                return;
              }
              return `$ ${formatNumberWithCommas(params.value)}`;
            },
          },
          {
            field: "DV01Rate",
            headerName: "DV01 Executed %",
            colId: "DV01Rate",
            maxWidth: 130,
            valueGetter: (params: any) => {
              const won = params.data.DV01Won;
              const seen = params.data.DV01Seen;
              const ratio = won / seen;
              const percentage = (ratio * 100).toFixed(2);

              return percentage;
            },
            cellRenderer: (params: any) => {
              if (!params.value) {
                return "";
              }
              return `${params.value} %`;
            },
          },
          {
            field: "spreadTotal",
            colId: "AverageSpread(bps)",
            headerName: "Average Spread (bps)",
            maxWidth: 140,
            valueGetter: (params: any) => {
              const value =
                params.data.spreadTotal / params.data.blocksWonTally;
              const rate = value;
              if (!rate) {
                return;
              }
              return Math.round(rate);
            },
            cellRenderer: (params: any) => {
              if (!params.value) {
                return "";
              }
              return `${params.value} bps`;
            },
          },
        ],
      };

      return res;
    };
  }, []);

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <div
        id="CPTableContainer"
        className={`${theme == "dark" ? "agChartCubeBorderDark" : "agChartCubeBorder"} flex h-1/2 p-2`}
      >
        <div className="flex w-full flex-col">
          <div
            id="CPReportTable"
            className={`${theme == "dark" ? "ag-theme-balham-dark" : "ag-theme-balham"} flex h-full w-full flex-col  `}
            style={style}
          >
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={colDefs}
              defaultColDef={defaultColDefAnalytics}
              columnMenu={"new"} //do not delete, affects header menus
              detailRowAutoHeight={true}
              getRowId={getRowId}
              // rowSelection={"single"}
              suppressAggFuncInHeader={true}
              suppressDragLeaveHidesColumns={true}
              // Style:
              rowHeight={globalAGGridTableHeight}
              scrollbarWidth={6}
              // groupDisplayType={"groupRows"}
              animateRows={true} // Smoother transition of rows when updates occur - needs 'getRowId' for this to work.
              maintainColumnOrder={true}
              // components={}
              tooltipShowDelay={1}
              domLayout="normal"
              // Have a drop down for exta data:
              masterDetail={true}
              detailCellRendererParams={detailCellRendererParams}
            />
          </div>
        </div>
      </div>
      <div className="flex h-1/2 w-full justify-end">
        <div
          id="notationalWonByCPContainer"
          className={`${theme == "dark" ? "agChartCubeBorderDark" : "agChartCubeBorder"} mt-3 flex h-[calc(100%-13px)] w-[calc(100%-10rem)] p-2`}
        >
          <div className="flex h-full w-full flex-col">
            <div>DV01 Won by Counterparty</div>
            <div id="NotionalByCounterpartyBar" className="flex h-full w-full">
              <CounterpartyBarChart filteredData={filteredData} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CounterpartyMainContent;
